import { AxiosError } from 'axios';

import { apiService } from '../../../shared/services/apiServices';
import { EMPLOYEE_ERROR_MESSAGES } from '../errors';
import employeeEndpoints from './employeeEndpoints';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CreateDraftEmployeePayload {
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  gender?: 'male' | 'female';
  academicTitle?: string;
}

export interface CreateDraftEmployeeResult {
  success: boolean;
  employeeId?: string;
  error?: string;
}

export interface CreateFinalizedEmployeeResult {
  success: boolean;
  employeeId?: string;
  userId?: string;
  error?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return EMPLOYEE_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const msg = apiError.message || apiError.error;
    if (msg === 'Internal Server Error') {
      return EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }
    if (msg?.startsWith('Access denied')) {
      return EMPLOYEE_ERROR_MESSAGES.ACCESS_DENIED;
    }
    if (msg?.toLowerCase().includes('email already exists')) {
      return EMPLOYEE_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }
    const limitNum = msg?.match(/employee limit of (\d+) reached/i)?.[1];
    if (limitNum) {
      return EMPLOYEE_ERROR_MESSAGES.LIMIT_REACHED(parseInt(limitNum, 10));
    }
    return msg || EMPLOYEE_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  return EMPLOYEE_ERROR_MESSAGES.DEFAULT_ERROR;
};

// ── createFinalizedEmployee ───────────────────────────────────────────────────

export const createFinalizedEmployee = async (
  facilityId: string,
  payload: CreateDraftEmployeePayload
): Promise<CreateFinalizedEmployeeResult> => {
  try {
    const result = await apiService.postAPI(
      employeeEndpoints.EMPLOYEE_FINALIZE(facilityId),
      payload
    );

    if (result.error) {
      return {
        success: false,
        error: result.isNetworkError
          ? EMPLOYEE_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data),
      };
    }

    const data = result.data as { employee: { id: string; userId: string } };
    return {
      success: true,
      employeeId: data.employee.id,
      userId: data.employee.userId,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      return {
        success: false,
        error: isNetworkError
          ? EMPLOYEE_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

// ── createDraftEmployee ───────────────────────────────────────────────────────

export const createDraftEmployee = async (
  facilityId: string,
  payload: CreateDraftEmployeePayload
): Promise<CreateDraftEmployeeResult> => {
  try {
    const result = await apiService.postAPI(
      employeeEndpoints.EMPLOYEE_DRAFT(facilityId),
      payload
    );

    if (result.error) {
      return {
        success: false,
        error: result.isNetworkError
          ? EMPLOYEE_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data),
      };
    }

    const data = result.data as { employee: { id: string } };
    return { success: true, employeeId: data.employee.id };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      return {
        success: false,
        error: isNetworkError
          ? EMPLOYEE_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EMPLOYEE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
